import { FACILITY_USER_ACCESS_MODE } from '@/constants/facilityUserAccessModes';
import {
  ALL_FACILITIES_PERMISSION_TARGET_ID,
  hasSelectedPermissions,
  toPermissionPayload,
} from '../UserManagement/components/facilityPermissionsPayload.utils';

const toInteger = (value) => parseInt(value, 10);

const buildFacilityPermissionsArrayFromUser = (user, accessMode) => {
  const facilityPermissionsArray = [];
  const shouldIncludeAllFacilitiesTarget =
    accessMode === FACILITY_USER_ACCESS_MODE.ALL_FACILITIES;

  if (shouldIncludeAllFacilitiesTarget) {
    const allFacilitiesPermissions = user.facility_and_permissions?.find(
      (fp) => fp.facilityId === null,
    )?.permissions;

    return [
      {
        facilityId: null,
        permissions: toPermissionPayload(allFacilitiesPermissions),
      },
    ];
  }

  user.facility_and_permissions?.forEach((fp) => {
    if (fp.facilityId === null) {
      return;
    }

    if (hasSelectedPermissions(fp.permissions)) {
      facilityPermissionsArray.push({
        facilityId: fp.facilityId,
        permissions: fp.permissions,
      });
    }
  });

  return facilityPermissionsArray;
};

const buildFacilityPermissionsArray = (facilityPermissions, accessMode) => {
  const facilityPermissionsArray = [];
  const shouldIncludeAllFacilitiesTarget =
    accessMode === FACILITY_USER_ACCESS_MODE.ALL_FACILITIES;

  if (shouldIncludeAllFacilitiesTarget) {
    return [
      {
        facilityId: null,
        permissions: toPermissionPayload(
          facilityPermissions[ALL_FACILITIES_PERMISSION_TARGET_ID],
        ),
      },
    ];
  }

  Object.entries(facilityPermissions).forEach(([facilityId, permissions]) => {
    if (facilityId === ALL_FACILITIES_PERMISSION_TARGET_ID) {
      return;
    }

    if (!isNaN(facilityId) && hasSelectedPermissions(permissions)) {
      facilityPermissionsArray.push({
        facilityId: toInteger(facilityId),
        permissions,
      });
    }
  });

  return facilityPermissionsArray;
};

const addOrganizationPermissions = (formData, organizationPermissions) => {
  Object.entries(organizationPermissions).forEach(([orgId, permissions]) => {
    if (!isNaN(orgId) && permissions && Object.keys(permissions).length > 0) {
      formData.organization_permissions = permissions;
    }
  });
};

export const buildBasicInfoFormData = ({
  basicInfo,
  permissionsType,
  selectedRoleId,
  accessMode,
  selectedFacilities,
  selectedOrganization,
  user,
}) => {
  if (permissionsType === 'role') {
    return {
      email: basicInfo.email,
      name: basicInfo.userName || basicInfo.name,
      mfa_type: basicInfo.mfa_type,
      language_preference: basicInfo.languagePreference,
      permissions_type: permissionsType,
      role_id: selectedRoleId,
    };
  }

  const formData = {
    email: basicInfo.email,
    name: basicInfo.userName || basicInfo.name,
    mfa_type: basicInfo.mfa_type,
    language_preference: basicInfo.languagePreference,
    has_all_facilities_access:
      accessMode === FACILITY_USER_ACCESS_MODE.ALL_FACILITIES,
    facility_ids:
      accessMode === FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES
        ? selectedFacilities.map(toInteger)
        : [],
    organization_id: selectedOrganization
      ? toInteger(selectedOrganization)
      : null,
    permissions_type: permissionsType,
  };

  const facilityPermissionsArray = buildFacilityPermissionsArrayFromUser(
    user,
    accessMode,
  );

  if (facilityPermissionsArray.length > 0) {
    formData.facility_permissions = facilityPermissionsArray;
  }

  if (user.organization_permissions?.permissions) {
    formData.organization_permissions =
      user.organization_permissions.permissions;
  }

  return formData;
};

export const buildPermissionsFormData = ({
  user,
  permissionsType,
  selectedRoleId,
  accessMode,
  selectedFacilities,
  selectedOrganization,
  facilityPermissions,
  organizationPermissions,
}) => {
  const formData = {
    email: user.user_data.email,
    name: user.user_data.name,
    mfa_type: user.user_data.mfa_type,
    language_preference: user.user_data.language_preference,
    has_all_facilities_access:
      accessMode === FACILITY_USER_ACCESS_MODE.ALL_FACILITIES,
    facility_ids:
      accessMode === FACILITY_USER_ACCESS_MODE.DIRECT_FACILITIES
        ? selectedFacilities.map(toInteger)
        : [],
    organization_id: selectedOrganization
      ? toInteger(selectedOrganization)
      : null,
    permissions_type: permissionsType,
  };

  if (permissionsType === 'role') {
    formData.role_id = selectedRoleId;
    return formData;
  }

  const facilityPermissionsArray = buildFacilityPermissionsArray(
    facilityPermissions,
    accessMode,
  );

  if (facilityPermissionsArray.length > 0) {
    formData.facility_permissions = facilityPermissionsArray;
  }

  addOrganizationPermissions(formData, organizationPermissions);

  return formData;
};

export const buildEmployeeInfoFormData = (employeeInfo) => ({
  first_name: employeeInfo.first_name,
  last_name: employeeInfo.last_name,
  employee_number: employeeInfo.employee_number,
  management_level: employeeInfo.management_level,
  approval_limit: employeeInfo.approval_limit,
  job_code: employeeInfo.job_code,
  job_title: employeeInfo.job_title,
  department: employeeInfo.department,
});

export const wasFacilityGroupScoped = (user) =>
  user?.user_data?.access_mode === FACILITY_USER_ACCESS_MODE.FACILITY_GROUPS ||
  (user?.assigned_facility_groups || []).length > 0;
